import { useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categoryArticles, categoryInfo, type CategoryArticleItem } from '@/mocks/category';

const allCategorySlugs = ['daily-contest', 'celebrity-scandal', 'blogger-contrast', 'trending'];
const articlesPerPage = 9;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);

  const activeSlug = slug || 'daily-contest';
  const info = categoryInfo[activeSlug] || categoryInfo['daily-contest'];

  const filteredArticles = useMemo(
    () => categoryArticles.filter((a) => a.categorySlug === activeSlug),
    [activeSlug]
  );

  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
  const pagedArticles = useMemo(
    () => filteredArticles.slice((currentPage - 1) * articlesPerPage, currentPage * articlesPerPage),
    [filteredArticles, currentPage]
  );

  const handleCategoryChange = (newSlug: string) => {
    setCurrentPage(1);
    navigate('/category/all');
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-18">
        {/* Category Header */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-[10px]" />
              <span className="text-primary-500 font-medium">{info.name}</span>
            </nav>

            <div className="flex items-center gap-4 mb-2">
              <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 text-lg">
                <i className={info.icon} />
              </span>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {info.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-0.5">{info.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="bg-background-50 border-b border-background-200 sticky top-16 md:top-18 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 py-3 overflow-x-auto">
              {allCategorySlugs.map((catSlug) => {
                const catInfo = categoryInfo[catSlug];
                const isActive = catSlug === activeSlug;
                return (
                  <button
                    key={catSlug}
                    onClick={() => handleCategoryChange(catSlug)}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                      isActive
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:bg-background-100 hover:text-primary-500'
                    }`}
                  >
                    <i className={`${catInfo.icon} text-sm`} />
                    {catInfo.name}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {pagedArticles.length === 0 ? (
              <div className="text-center py-20">
                <span className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 text-2xl mx-auto mb-4">
                  <i className="ri-inbox-line" />
                </span>
                <p className="text-sm text-foreground-500">暂无相关内容</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {pagedArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-sm text-foreground-500 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      aria-label="上一页"
                    >
                      <i className="ri-arrow-left-s-line" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                          page === currentPage
                            ? 'bg-primary-500 text-background-50'
                            : 'text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-sm text-foreground-500 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      aria-label="下一页"
                    >
                      <i className="ri-arrow-right-s-line" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* SEO structured data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: `${info.name} - 吃瓜网站`,
              description: info.description,
              url: 'http://chigua91.cn/category/all',
              isPartOf: {
                '@type': 'WebSite',
                name: '吃瓜网站',
                url: 'http://chigua91.cn/',
              },
              breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  {
                    '@type': 'ListItem',
                    position: 1,
                    name: '首页',
                    item: 'http://chigua91.cn/',
                  },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: info.name,
                    item: 'http://chigua91.cn/category/all',
                  },
                ],
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}

function ArticleCard({ article }: { article: CategoryArticleItem }) {
  return (
    <Link
      to="/category/all"
      className="bg-background-100 rounded-xl overflow-hidden group cursor-pointer"
    >
      <div className="relative w-full aspect-[4/3] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          title={`${article.title} - 吃瓜网站${article.category}`}
        />
        {article.tag && (
          <span className={`absolute top-3 left-3 px-2 py-0.5 rounded-md text-[10px] font-bold text-background-50 ${
            article.tag === '爆' ? 'bg-primary-500' : article.tag === '热' ? 'bg-accent-500' : 'bg-secondary-500'
          }`}>
            {article.tag}
          </span>
        )}
        <span className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md bg-background-950/70 text-background-50 text-[11px]">
          {article.category}
        </span>
      </div>
      <div className="p-4">
        <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug mb-2 group-hover:text-primary-500 transition-colors">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-[11px] text-foreground-400">
          <div className="flex items-center gap-1">
            <i className="ri-eye-line" />
            <span>{article.views}</span>
          </div>
          <div className="flex items-center gap-1">
            <i className="ri-time-line" />
            <span>{article.date}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}